import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Info } from "lucide-react";

export interface ConfidenceBreakdownData {
  overall: number;
  sourceQuality: number;
  relevance: number;
  resultCount: number;
  evidenceQuality: number;
  sources?: string[];
}

interface ConfidenceBreakdownProps {
  confidence: number | null | undefined;
  breakdown?: ConfidenceBreakdownData | null;
  sources?: string[];
  showInfoIcon?: boolean;
  className?: string;
}

/**
 * Confidence score with hover breakdown explanation
 * Shows how the score is calculated based on:
 * - Relevance (40%): Vector similarity scores
 * - Source Quality (30%): Trust hierarchy
 * - Evidence Quality (20%): Metadata richness
 * - Result Count (10%): Number of results
 */
export function ConfidenceBreakdown({
  confidence,
  breakdown,
  sources = [],
  showInfoIcon = true,
  className = "",
}: ConfidenceBreakdownProps) {
  // Handle null/undefined confidence
  const safeConfidence = confidence ?? 0;
  const percentage = (safeConfidence * 100).toFixed(1);

  // Determine confidence level
  const level = safeConfidence >= 0.8 ? "High" : safeConfidence >= 0.6 ? "Medium" : safeConfidence >= 0.4 ? "Low" : "Very Low";
  const levelColor = safeConfidence >= 0.8 ? "text-green-600" : safeConfidence >= 0.6 ? "text-yellow-600" : safeConfidence >= 0.4 ? "text-orange-600" : "text-red-600";

  // Use breakdown sources if available, otherwise fall back to props
  const displaySources = breakdown?.sources || sources;

  // Check if we have real breakdown data
  const hasBreakdown = breakdown && (breakdown.relevance > 0 || breakdown.sourceQuality > 0);

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span className={`inline-flex items-center gap-1 cursor-help ${className}`}>
          <span>{percentage}%</span>
          {showInfoIcon && <Info className="h-3 w-3 text-muted-foreground" />}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-80" side="top">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Confidence Score</h4>
            <span className={`text-sm font-medium ${levelColor}`}>{level}</span>
          </div>

          <div className="text-xs text-muted-foreground">
            {hasBreakdown ? "Actual component scores:" : "Score is calculated from weighted components:"}
          </div>

          {/* Component breakdown */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Relevance (40%)</span>
              <span className="font-medium">
                {hasBreakdown ? `${(breakdown.relevance * 100).toFixed(1)}%` : "—"}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-blue-500 h-1.5 rounded-full transition-all"
                style={{ width: hasBreakdown ? `${breakdown.relevance * 100}%` : '0%' }}
              />
            </div>
            <p className="text-[10px] text-muted-foreground">
              Vector similarity between query and retrieved docs
            </p>

            <div className="flex items-center justify-between text-xs mt-2">
              <span className="text-muted-foreground">Source Quality (30%)</span>
              <span className="font-medium">
                {hasBreakdown ? `${(breakdown.sourceQuality * 100).toFixed(1)}%` : "—"}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-green-500 h-1.5 rounded-full transition-all"
                style={{ width: hasBreakdown ? `${breakdown.sourceQuality * 100}%` : '0%' }}
              />
            </div>
            <p className="text-[10px] text-muted-foreground">
              Trust hierarchy: Golden (100%) &gt; Help (90%) &gt; Linear (50%) &gt; Notion (30%) &gt; Slack (20%)
            </p>

            <div className="flex items-center justify-between text-xs mt-2">
              <span className="text-muted-foreground">Evidence Quality (20%)</span>
              <span className="font-medium">
                {hasBreakdown ? `${(breakdown.evidenceQuality * 100).toFixed(1)}%` : "—"}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-purple-500 h-1.5 rounded-full transition-all"
                style={{ width: hasBreakdown ? `${breakdown.evidenceQuality * 100}%` : '0%' }}
              />
            </div>
            <p className="text-[10px] text-muted-foreground">
              Metadata richness: URL, title, timestamp, section
            </p>

            <div className="flex items-center justify-between text-xs mt-2">
              <span className="text-muted-foreground">Result Count (10%)</span>
              <span className="font-medium">
                {hasBreakdown ? `${breakdown.resultCount} results` : "—"}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-orange-500 h-1.5 rounded-full transition-all"
                style={{ width: hasBreakdown ? `${Math.min(breakdown.resultCount / 10, 1) * 100}%` : '0%' }}
              />
            </div>
            <p className="text-[10px] text-muted-foreground">
              More results = higher confidence (max at 10)
            </p>
          </div>

          {/* Sources used */}
          {displaySources.length > 0 && (
            <div className="pt-2 border-t">
              <p className="text-[10px] text-muted-foreground mb-1">Sources used:</p>
              <div className="flex flex-wrap gap-1">
                {displaySources.map((source, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 bg-muted rounded">
                    {source}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2 border-t">
            <p className="text-[10px] text-muted-foreground">
              Minimum threshold: 65%. Below this, responds "I don't know".
            </p>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

/**
 * Simple badge version with hover for list views
 */
export function ConfidenceBadge({
  confidence,
  breakdown,
  sources = [],
}: {
  confidence: number | null | undefined;
  breakdown?: ConfidenceBreakdownData | null;
  sources?: string[];
}) {
  // Handle null/undefined confidence
  const safeConfidence = confidence ?? 0;
  const percentage = (safeConfidence * 100).toFixed(0);

  // Use breakdown sources if available, otherwise fall back to props
  const displaySources = breakdown?.sources || sources;

  // Check if we have real breakdown data
  const hasBreakdown = breakdown && (breakdown.relevance > 0 || breakdown.sourceQuality > 0);

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span className="inline-flex items-center text-xs border rounded-md px-2 py-0.5 cursor-help hover:bg-muted transition-colors">
          {percentage}%
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-72" side="top">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Confidence: {percentage}%</h4>
          </div>

          {hasBreakdown ? (
            <div className="text-xs space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Relevance (40%)</span>
                <span className="font-medium">{(breakdown.relevance * 100).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Source Quality (30%)</span>
                <span className="font-medium">{(breakdown.sourceQuality * 100).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Evidence (20%)</span>
                <span className="font-medium">{(breakdown.evidenceQuality * 100).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Results (10%)</span>
                <span className="font-medium">{breakdown.resultCount} matches</span>
              </div>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground space-y-1">
              <p><span className="font-medium">Relevance (40%):</span> Query-doc similarity</p>
              <p><span className="font-medium">Source Quality (30%):</span> Source trust level</p>
              <p><span className="font-medium">Evidence (20%):</span> Metadata richness</p>
              <p><span className="font-medium">Results (10%):</span> Number of matches</p>
            </div>
          )}

          {displaySources.length > 0 && (
            <div className="pt-1 border-t">
              <p className="text-[10px] text-muted-foreground">
                Sources: {displaySources.join(', ')}
              </p>
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

export default ConfidenceBreakdown;
